"use client";

import { cn } from "./utils";
import { motion } from "motion/react";
import { ChevronRight } from "lucide-react";

export interface RaiseComparisonItem {
  id: string;
  name: string;
  status: "active" | "paused" | "closed" | "draft";
  current: number;
  goal: number;
  fundsCount: number;
  onClick?: () => void;
}

export interface FundraisingRaiseComparisonProps {
  raises: RaiseComparisonItem[];
  formatCurrency?: (value: number) => string;
  className?: string;
}

/**
 * A component for comparing multiple fundraising campaigns.
 * Shows progress bars, status badges, and key metrics for each raise.
 */
export function FundraisingRaiseComparison({
  raises,
  formatCurrency = defaultFormatCurrency,
  className,
}: FundraisingRaiseComparisonProps) {
  return (
    <div className={cn("divide-y divide-neutral-100", className)}>
      {raises.map((raise, index) => {
        const progress = raise.goal > 0 ? (raise.current / raise.goal) * 100 : 0;
        const isComplete = progress >= 100;

        return (
          <motion.div
            key={raise.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={cn(
              "p-4 transition-colors",
              raise.onClick && "hover:bg-neutral-50 cursor-pointer"
            )}
            onClick={raise.onClick}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">{raise.name}</span>
                <StatusBadge status={raise.status} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{progress.toFixed(0)}%</span>
                {raise.onClick && <ChevronRight className="w-4 h-4 text-neutral-300" />}
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-neutral-100 rounded-full overflow-hidden mb-2">
              <motion.div
                className={cn(
                  "h-full rounded-full",
                  isComplete ? "bg-emerald-500" : "bg-blue-500"
                )}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between text-xs text-neutral-500">
              <span>{raise.fundsCount} funds</span>
              <span>
                {formatCurrency(raise.current)} / {formatCurrency(raise.goal)}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }: { status: RaiseComparisonItem["status"] }) {
  const getStatusStyles = () => {
    switch (status) {
      case "active":
        return "bg-emerald-100 text-emerald-700";
      case "paused":
        return "bg-amber-100 text-amber-700";
      case "closed":
        return "bg-neutral-100 text-neutral-500";
      case "draft":
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  return (
    <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full capitalize", getStatusStyles())}>
      {status}
    </span>
  );
}

function defaultFormatCurrency(value: number): string {
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
